import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { orgSettingsService } from '@/services/orgSettings'
import { useAuthStore } from '@/stores/auth'

export const orgPageViewCaptureKeys = {
  all: (orgId?: string) => ['orgPageViewCapture', orgId] as const,
  detail: (orgId?: string) =>
    [...orgPageViewCaptureKeys.all(orgId), 'detail'] as const,
}

export function useOrgPageViewCapture() {
  const { organization } = useAuthStore()

  const { data, isLoading, error } = useQuery({
    queryKey: orgPageViewCaptureKeys.detail(organization?.id),
    queryFn: () => orgSettingsService.getPageViewCapture(),
    enabled: !!organization?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return {
    enabled: data ?? false,
    isLoading,
    error,
  }
}

export function useUpdateOrgPageViewCapture() {
  const queryClient = useQueryClient()
  const { organization } = useAuthStore()
  const detailKey = orgPageViewCaptureKeys.detail(organization?.id)

  return useMutation({
    mutationFn: (enabled: boolean) =>
      orgSettingsService.updatePageViewCapture(enabled),
    onMutate: async (enabled: boolean) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<boolean>(detailKey)

      queryClient.setQueryData<boolean>(detailKey, enabled)

      return { previous }
    },
    onError: (_err, _enabled, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(detailKey, context.previous)
      }

      toast.error('Failed to update page view capture setting')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: orgPageViewCaptureKeys.all(organization?.id),
      })
    },
  })
}
